import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQueries } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import LocalShippingRoundedIcon from "@mui/icons-material/LocalShippingRounded";
import StoreMallDirectoryRoundedIcon from "@mui/icons-material/StoreMallDirectoryRounded";
import { OrderMenuProductCard } from "@/components/order/OrderMenuProductCard";
import { AllergenChip } from "@/components/ui/AllergenChip";
import { format, addDays, parseISO } from "date-fns";
import { fr } from "date-fns/locale";
import { ClientLayout } from "../components/ClientLayout";
import {
  clientApi,
  type ClientAddress,
  type ClientMenu,
  type ClientMenuSlot,
  type PickupLocation,
} from "../services/clientApi.service";
import { getBusinessNow, getBusinessTodayDate } from "@/utils/businessTime";
import { computeDeliveryFee } from "@/utils/computeDeliveryFee";
import type { ClientProfile } from "../services/clientApi.service";
import { buildAddressFormPath } from "../utils/addressFormReturn";
import type { ClientOrderReplayState } from "../utils/orderReplay";
import {
  isAddressDeliverable,
  OUT_OF_DELIVERY_ZONE_MESSAGE,
} from "@/utils/addressDeliverability";
import { formatLocationLine } from "@/utils/location";
import { formatPhoneDisplay } from "@/constants/phoneDialCodes";

type ServiceType = "MEAL_DELIVERY" | "CATERING";

const SLOT_TYPE_LABELS: Record<string, string> = {
  ENTREE: "Entrées",
  PLAT_VIANDE: "Plats viande",
  PLAT_POISSON: "Plats poisson",
  PLAT_VEGE: "Plats végétariens",
  PLAT_AUTRE: "Plats",
  LEGUMES_100: "100 % légumes",
  ACCOMPAGNEMENT: "Accompagnements",
  DESSERT: "Desserts",
  BOISSON: "Boissons",
};

/**
 * Calcule la première date de livraison/retrait possible selon la deadline 10h Cayenne :
 * - avant 10h : aujourd'hui ;
 * - 10h ou après : demain.
 */
function getMinDeliveryDate(): string {
  const now = getBusinessNow();
  const today = getBusinessTodayDate();
  const isAfterDeadline = now.getHours() >= 10;
  const minDate = isAfterDeadline ? addDays(today, 1) : today;
  return format(minDate, "yyyy-MM-dd");
}

interface CartItem {
  productId: string;
  productName: string;
  unitPrice: number;
  quantity: number;
  /** Formule Switch demandée par le client (sans surcoût). */
  isSwitch?: boolean;
}

export function ClientOrderPage(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const returnAddressIdRef = useRef(
    (location.state as { selectedAddressId?: string } | null)?.selectedAddressId,
  );
  const replayRef = useRef(
    (location.state as { replay?: ClientOrderReplayState } | null)?.replay,
  );
  const replayAppliedRef = useRef(false);
  const [activeStep, setActiveStep] = useState(0);

  // Step 1
  const [serviceType, setServiceType] = useState<ServiceType>("MEAL_DELIVERY");
  const [deliveryDate, setDeliveryDate] = useState<string>(getMinDeliveryDate());
  const [addresses, setAddresses] = useState<ClientAddress[]>([]);
  const [selectedAddressId, setSelectedAddressId] = useState<string>("");
  const [pickup, setPickup] = useState<PickupLocation | null>(null);
  const [addressesLoading, setAddressesLoading] = useState(true);

  // Step 2
  const [menu, setMenu] = useState<ClientMenu | null>(null);
  const [menuLoading, setMenuLoading] = useState(false);
  const [menuError, setMenuError] = useState<string | null>(null);
  const [profile, setProfile] = useState<ClientProfile | null>(null);
  const [customerAllergenIds, setCustomerAllergenIds] = useState<string[]>([]);
  const [cartByDate, setCartByDate] = useState<Record<string, Record<string, CartItem>>>({});

  const [instructionsByDate, setInstructionsByDate] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [paymentTiming, setPaymentTiming] = useState<"IMMEDIATE" | "ON_RECEPTION">(
    "IMMEDIATE",
  );

  const minDate = useMemo(getMinDeliveryDate, []);
  const isAtMinDeliveryDate = deliveryDate <= minDate;
  const cartForDate = useMemo(() => cartByDate[deliveryDate] ?? {}, [cartByDate, deliveryDate]);
  const cartDates = useMemo(
    () => Object.keys(cartByDate).sort((a, b) => a.localeCompare(b)),
    [cartByDate],
  );
  const allCartItems = useMemo(
    () => cartDates.flatMap((d) => Object.values(cartByDate[d] ?? {})),
    [cartDates, cartByDate],
  );

  useEffect(() => {
    let cancelled = false;
    async function load(): Promise<void> {
      setAddressesLoading(true);
      try {
        const [addrs, pickupLocation, profile] = await Promise.all([
          clientApi.listAddresses(),
          clientApi.getPickupLocation().catch(() => null),
          clientApi.getMe(),
        ]);
        if (cancelled) return;
        setAddresses(addrs);
        setPickup(pickupLocation);
        setProfile(profile);
        setCustomerAllergenIds(profile.customer.allergens.map((a) => a.id));
        const returnSelection = returnAddressIdRef.current;
        if (returnSelection && addrs.some((a) => a.id === returnSelection)) {
          setSelectedAddressId(returnSelection);
          returnAddressIdRef.current = undefined;
          navigate("/client/order/new", { replace: true, state: null });
        } else {
          const def = addrs.find((a) => a.isDefault) ?? addrs[0];
          if (def) setSelectedAddressId(def.id);
        }
      } finally {
        if (!cancelled) setAddressesLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  useEffect(() => {
    const replay = replayRef.current;
    if (!replay || replayAppliedRef.current) return;
    replayAppliedRef.current = true;
    replayRef.current = undefined;

    setServiceType(replay.serviceType);
    if (replay.customerAddressId) {
      setSelectedAddressId(replay.customerAddressId);
    }

    const nextCart: Record<string, Record<string, CartItem>> = {};
    const nextInstructions: Record<string, string> = {};
    for (const day of replay.orders) {
      const dayCart: Record<string, CartItem> = {};
      for (const it of day.items) {
        dayCart[it.productId] = {
          productId: it.productId,
          productName: it.productName,
          unitPrice: it.unitPrice,
          quantity: it.quantity,
          isSwitch: it.isSwitch ?? false,
        };
      }
      nextCart[day.deliveryDate] = dayCart;
      if (day.specialInstructions?.trim()) {
        nextInstructions[day.deliveryDate] = day.specialInstructions.trim();
      }
    }
    setCartByDate(nextCart);
    setInstructionsByDate(nextInstructions);

    const firstDate = replay.orders[0]?.deliveryDate;
    if (firstDate && firstDate >= minDate) {
      setDeliveryDate(firstDate);
    } else if (firstDate) {
      setDeliveryDate(minDate);
    }

    navigate("/client/order/new", { replace: true, state: null });
  }, [minDate, navigate]);

  useEffect(() => {
    if (activeStep !== 1) return;
    let cancelled = false;
    async function loadMenu(): Promise<void> {
      setMenuLoading(true);
      setMenuError(null);
      try {
        const m = await clientApi.getMenu(deliveryDate);
        if (cancelled) return;
        setMenu(m);
        if (!m) {
          setMenuError(
            "Aucun menu publié n'est disponible pour cette date. Choisissez un autre jour.",
          );
        }
      } catch (err) {
        if (cancelled) return;
        const message =
          (err as { response?: { data?: { message?: string } } }).response?.data?.message ??
          "Impossible de charger le menu.";
        setMenuError(message);
      } finally {
        if (!cancelled) setMenuLoading(false);
      }
    }
    void loadMenu();
    return () => {
      cancelled = true;
    };
  }, [activeStep, deliveryDate]);

  const productAvailabilityById = useMemo(() => {
    const map: Record<
      string,
      { estimated: number; orderedQuantity: number; remainingQuantity: number }
    > = {};
    if (!menu) return map;
    for (const slot of menu.slots) {
      if (slot.product?.availability) {
        map[slot.product.id] = slot.product.availability;
      }
    }
    return map;
  }, [menu]);

  /** Plafond panier pour la date affichée (estimé − commandes déjà passées). */
  function maxCartQuantityForProduct(productId: string): number | null {
    const availability = productAvailabilityById[productId];
    if (!availability) return null;
    return Math.max(0, availability.estimated - availability.orderedQuantity);
  }

  function canAddOneToCart(productId: string): boolean {
    const max = maxCartQuantityForProduct(productId);
    if (max === null) return true;
    const inCart = cartForDate[productId]?.quantity ?? 0;
    return inCart < max;
  }

  function isProductExhausted(productId: string): boolean {
    const availability = productAvailabilityById[productId];
    if (!availability) return false;
    return availability.remainingQuantity <= 0;
  }

  function addToCart(slot: ClientMenuSlot): void {
    if (!slot.product) return;
    if (!canAddOneToCart(slot.product.id)) return;
    setCartByDate((prev) => {
      const prevDay = prev[deliveryDate] ?? {};
      const current = prevDay[slot.product!.id];
      return {
        ...prev,
        [deliveryDate]: {
          ...prevDay,
          [slot.product!.id]: {
            productId: slot.product!.id,
            productName: slot.product!.name,
            unitPrice: slot.product!.price,
            quantity: current ? current.quantity + 1 : 1,
            isSwitch: current?.isSwitch ?? false,
          },
        },
      };
    });
  }

  function toggleSwitch(productId: string): void {
    setCartByDate((prev) => {
      const prevDay = prev[deliveryDate] ?? {};
      const item = prevDay[productId];
      if (!item) return prev;
      return {
        ...prev,
        [deliveryDate]: {
          ...prevDay,
          [productId]: { ...item, isSwitch: !item.isSwitch },
        },
      };
    });
  }

  function decrement(productId: string): void {
    setCartByDate((prev) => {
      const prevDay = prev[deliveryDate] ?? {};
      const item = prevDay[productId];
      if (!item) return prev;
      const nextDay = { ...prevDay };
      if (item.quantity <= 1) {
        delete nextDay[productId];
      } else {
        nextDay[productId] = { ...item, quantity: item.quantity - 1 };
      }
      const next = { ...prev };
      if (Object.keys(nextDay).length === 0) {
        delete next[deliveryDate];
        return next;
      }
      next[deliveryDate] = nextDay;
      return next;
    });
  }

  const subtotal = useMemo(
    () => allCartItems.reduce((sum, it) => sum + it.unitPrice * it.quantity, 0),
    [allCartItems],
  );
  const totalQuantity = useMemo(
    () => allCartItems.reduce((sum, it) => sum + it.quantity, 0),
    [allCartItems],
  );

  const selectedAddress = useMemo(
    () => addresses.find((a) => a.id === selectedAddressId) ?? null,
    [addresses, selectedAddressId],
  );

  const selectedAddressDeliverable = useMemo(
    () => (selectedAddress ? isAddressDeliverable(selectedAddress) : false),
    [selectedAddress],
  );

  const hasDeliverableAddress = useMemo(
    () => addresses.some(isAddressDeliverable),
    [addresses],
  );

  useEffect(() => {
    if (serviceType !== "MEAL_DELIVERY") return;
    if (!selectedAddressId) return;
    if (!selectedAddressDeliverable) {
      setServiceType("CATERING");
    }
  }, [serviceType, selectedAddressId, selectedAddressDeliverable]);

  const zoneDeliveryCost = useMemo(() => {
    if (serviceType !== "MEAL_DELIVERY") return 0;
    return Number(selectedAddress?.zone?.deliveryCost) || 0;
  }, [serviceType, selectedAddress]);

  const deliveryFeePerOrder = useMemo(
    () => computeDeliveryFee(serviceType, zoneDeliveryCost, profile?.customer ?? null),
    [serviceType, zoneDeliveryCost, profile],
  );

  const deliveryTotal = useMemo(
    () =>
      serviceType === "MEAL_DELIVERY" ? deliveryFeePerOrder * cartDates.length : 0,
    [serviceType, deliveryFeePerOrder, cartDates.length],
  );

  const grandTotal = useMemo(() => subtotal + deliveryTotal, [subtotal, deliveryTotal]);

  function canGoNext(): boolean {
    if (activeStep === 0) {
      if (serviceType === "MEAL_DELIVERY") {
        return Boolean(selectedAddressId && selectedAddressDeliverable);
      }
      return Boolean(pickup);
    }
    if (activeStep === 1) return allCartItems.length > 0;
    if (activeStep === 2) return true;
    return false;
  }

  function handleNext(): void {
    setActiveStep((s) => Math.min(s + 1, 3));
  }
  function handleBack(): void {
    setActiveStep((s) => Math.max(s - 1, 0));
  }

  function changeDeliveryDate(deltaDays: number): void {
    const nextDate = format(addDays(parseISO(deliveryDate), deltaDays), "yyyy-MM-dd");
    if (nextDate < minDate) return;
    setDeliveryDate(nextDate);
  }

  function editDayMenu(date: string): void {
    setDeliveryDate(date);
    setActiveStep(1);
  }

  function setDayInstructions(date: string, value: string): void {
    setInstructionsByDate((prev) => {
      if (!value.trim()) {
        const next = { ...prev };
        delete next[date];
        return next;
      }
      return { ...prev, [date]: value };
    });
  }

  // Compte « en compte » : toujours en paiement différé, aucun choix proposé.
  const isOnAccount = profile?.customer.defaultPaymentMode === "EN_COMPTE";
  // Le client peut choisir entre payer maintenant (Stripe) ou à réception.
  const canChoosePaymentTiming = !isOnAccount;
  const payNow = canChoosePaymentTiming && paymentTiming === "IMMEDIATE";

  async function handleConfirm(): Promise<void> {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const createdOrderIds: string[] = [];
      // Une commande par jour (permet de commander toute la semaine d’un coup)
      for (const date of cartDates) {
        const items = Object.values(cartByDate[date] ?? {});
        if (items.length === 0) continue;
        const order = await clientApi.createOrder({
          serviceType,
          deliveryDate: date,
          customerAddressId: serviceType === "MEAL_DELIVERY" ? selectedAddressId : null,
          specialInstructions: instructionsByDate[date]?.trim() || null,
          items: items.map((it) => ({
            productId: it.productId,
            quantity: it.quantity,
            unitPrice: it.unitPrice,
            isSwitch: it.isSwitch ?? false,
          })),
        });
        createdOrderIds.push(order.id);
      }

      if (payNow && createdOrderIds.length > 0) {
        const checkout = await clientApi.createCheckout(createdOrderIds);
        window.location.assign(checkout.url);
        return;
      }

      navigate(`/client/orders`, { replace: true });
    } catch (err) {
      const message =
        (err as { response?: { data?: { message?: string } } }).response?.data?.message ??
        "Impossible de créer votre commande.";
      setSubmitError(message);
    } finally {
      setSubmitting(false);
    }
  }

  const slotGroups = useMemo(() => {
    if (!menu) return [] as Array<{ type: string; slots: ClientMenuSlot[] }>;
    const groups = new Map<string, ClientMenuSlot[]>();
    for (const s of menu.slots) {
      if (!s.product) continue;
      const list = groups.get(s.slotType) ?? [];
      list.push(s);
      groups.set(s.slotType, list);
    }
    return Array.from(groups.entries()).map(([type, slots]) => ({ type, slots }));
  }, [menu]);

  const displayedProductIds = useMemo(() => {
    if (!menu) return [] as string[];
    return [
      ...new Set(
        menu.slots.map((s) => s.product?.id).filter((id): id is string => Boolean(id)),
      ),
    ];
  }, [menu]);

  const hasOrderableProducts = displayedProductIds.length > 0;

  const productIdsForAllergens = useMemo(() => {
    const ids = new Set(displayedProductIds);
    for (const item of allCartItems) {
      ids.add(item.productId);
    }
    return [...ids];
  }, [displayedProductIds, allCartItems]);

  const productAllergenQueries = useQueries({
    queries: productIdsForAllergens.map((productId) => ({
      queryKey: ["client-product-allergens", productId],
      queryFn: () => clientApi.getProductAllergens(productId),
      enabled: activeStep >= 1 && activeStep <= 3 && Boolean(productId),
      staleTime: 60_000,
    })),
  });

  const productAllergensMap = useMemo(() => {
    const map: Record<
      string,
      Array<{ id: string; name: string; code: string; icon: string | null }>
    > = {};
    productIdsForAllergens.forEach((productId, index) => {
      const fromQuery = productAllergenQueries[index]?.data;
      const fromMenu = menu?.slots.find((s) => s.product?.id === productId)?.product
        ?.allergens;
      map[productId] =
        fromQuery && fromQuery.length > 0 ? fromQuery : (fromMenu ?? []);
    });
    return map;
  }, [productIdsForAllergens, productAllergenQueries, menu]);

  const allergenConflicts = useMemo(() => {
    if (customerAllergenIds.length === 0) return [];
    const conflicts: string[] = [];
    for (const item of allCartItems) {
      const productAllergens = productAllergensMap[item.productId] ?? [];
      const matching = productAllergens.filter((a) => customerAllergenIds.includes(a.id));
      if (matching.length > 0) {
        conflicts.push(
          `${item.productName} : ${matching.map((a) => a.name).join(", ")}`,
        );
      }
    }
    return conflicts;
  }, [allCartItems, productAllergensMap, customerAllergenIds]);

  return (
    <ClientLayout title="Nouvelle commande">
      <Stack spacing={2}>
        <Stepper activeStep={activeStep} alternativeLabel>
          <Step>
            <StepLabel>Mode</StepLabel>
          </Step>
          <Step>
            <StepLabel>Menu</StepLabel>
          </Step>
          <Step>
            <StepLabel>Récap</StepLabel>
          </Step>
          <Step>
            <StepLabel>Confirmer</StepLabel>
          </Step>
        </Stepper>

        {activeStep === 0 ? (
          <Stack spacing={2}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Mode et lieu
            </Typography>

            <Stack direction="row" spacing={1.5}>
              <Paper
                variant={serviceType === "MEAL_DELIVERY" ? "elevation" : "outlined"}
                onClick={() => {
                  if (!hasDeliverableAddress) return;
                  if (selectedAddressId && !selectedAddressDeliverable) return;
                  setServiceType("MEAL_DELIVERY");
                }}
                sx={{
                  flex: 1,
                  p: 1.5,
                  cursor: hasDeliverableAddress ? "pointer" : "not-allowed",
                  opacity: hasDeliverableAddress ? 1 : 0.55,
                  borderColor: serviceType === "MEAL_DELIVERY" ? "secondary.main" : undefined,
                  borderWidth: serviceType === "MEAL_DELIVERY" ? 2 : 1,
                  borderStyle: "solid",
                }}
              >
                <Stack alignItems="center" spacing={0.5}>
                  <LocalShippingRoundedIcon color="secondary" />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Livraison
                  </Typography>
                </Stack>
              </Paper>
              <Paper
                variant={serviceType === "CATERING" ? "elevation" : "outlined"}
                onClick={() => setServiceType("CATERING")}
                sx={{
                  flex: 1,
                  p: 1.5,
                  cursor: "pointer",
                  borderColor: serviceType === "CATERING" ? "secondary.main" : undefined,
                  borderWidth: serviceType === "CATERING" ? 2 : 1,
                  borderStyle: "solid",
                }}
              >
                <Stack alignItems="center" spacing={0.5}>
                  <StoreMallDirectoryRoundedIcon color="secondary" />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Retrait sur place
                  </Typography>
                </Stack>
              </Paper>
            </Stack>

            {!hasDeliverableAddress && !addressesLoading && addresses.length > 0 ? (
              <Alert severity="info">
                Aucune de vos adresses n&apos;est dans une zone de livraison. Commandez en retrait
                sur place.
              </Alert>
            ) : null}

            {serviceType === "MEAL_DELIVERY" ? (
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Adresse de livraison
                </Typography>
                {addressesLoading ? (
                  <CircularProgress size={20} />
                ) : addresses.length === 0 ? (
                  <Stack spacing={1}>
                    <Typography variant="body2" color="text.secondary">
                      Aucune adresse enregistrée.
                    </Typography>
                    <Button
                      onClick={() => navigate(buildAddressFormPath("new", "order"))}
                      size="small"
                      variant="outlined"
                      startIcon={<AddRoundedIcon />}
                    >
                      Ajouter une adresse
                    </Button>
                  </Stack>
                ) : (
                  <Stack spacing={1.5}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="addr-label">Adresse</InputLabel>
                      <Select
                        labelId="addr-label"
                        label="Adresse"
                        value={selectedAddressId}
                        onChange={(e) => setSelectedAddressId(e.target.value as string)}
                      >
                        {addresses.map((a) => (
                          <MenuItem key={a.id} value={a.id}>
                            {(a.label ? `${a.label} — ` : "") + a.address}
                            {!isAddressDeliverable(a) ? " (hors zone)" : ""}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {selectedAddress && !selectedAddressDeliverable ? (
                      <Alert severity="warning">{OUT_OF_DELIVERY_ZONE_MESSAGE}</Alert>
                    ) : null}
                    {selectedAddressDeliverable && selectedAddress?.zone ? (
                      <Typography variant="caption" color="text.secondary">
                        Zone : {selectedAddress.zone.name}
                      </Typography>
                    ) : null}
                    <Button
                      onClick={() => navigate(buildAddressFormPath("new", "order"))}
                      size="small"
                      variant="text"
                      startIcon={<AddRoundedIcon />}
                      sx={{ alignSelf: "flex-start", px: 0, minWidth: 0 }}
                    >
                      Ajouter une adresse
                    </Button>
                  </Stack>
                )}
              </Paper>
            ) : (
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Lieu de retrait
                </Typography>
                {pickup ? (
                  <Stack spacing={0.5}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {pickup.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {pickup.address}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {formatLocationLine(pickup.postalCode, pickup.city)}
                    </Typography>
                    {pickup.phone ? (
                      <Typography variant="caption" color="text.secondary">
                        Tél. {formatPhoneDisplay(pickup.phoneDialCode, pickup.phone)}
                      </Typography>
                    ) : null}
                  </Stack>
                ) : (
                  <Alert severity="warning">Adresse de retrait non configurée.</Alert>
                )}
              </Paper>
            )}
          </Stack>
        ) : null}

        {activeStep === 1 ? (
          <Stack spacing={2}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Button
                size="small"
                variant="outlined"
                onClick={() => changeDeliveryDate(-1)}
                disabled={isAtMinDeliveryDate}
              >
                Jour précédent
              </Button>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, textAlign: "center" }}>
                  Menu du {format(parseISO(deliveryDate), "EEEE d MMMM", { locale: fr })}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", textAlign: "center" }}>
                  {serviceType === "CATERING" ? "Retrait" : "Livraison"} ·{" "}
                  {format(parseISO(deliveryDate), "dd/MM/yyyy")}
                </Typography>
              </Box>
              <Button
                size="small"
                variant="outlined"
                onClick={() => changeDeliveryDate(1)}
              >
                Jour suivant
              </Button>
            </Stack>
            {menuError ? <Alert severity="warning">{menuError}</Alert> : null}
            {menuLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                <CircularProgress size={24} />
              </Box>
            ) : null}
            {!menuLoading && menu && !hasOrderableProducts ? (
              <Alert severity="info">
                Aucun produit n&apos;est disponible à la commande pour le{" "}
                {format(parseISO(deliveryDate), "EEEE d MMMM", { locale: fr })}. Choisissez un
                autre jour avec les boutons ci-dessus.
              </Alert>
            ) : null}
            {!menuLoading && menu && hasOrderableProducts ? (
              <Stack spacing={2}>
                {slotGroups.map((group) => (
                  <Box key={group.type}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      {SLOT_TYPE_LABELS[group.type] ?? group.type}
                    </Typography>
                    <Stack spacing={1}>
                      {group.slots.map((slot) => {
                        const product = slot.product!;
                        const inCart = cartForDate[product.id]?.quantity ?? 0;
                        const maxInCart = maxCartQuantityForProduct(product.id);
                        const exhausted = isProductExhausted(product.id);
                        const addDisabled =
                          maxInCart !== null && inCart >= maxInCart && !exhausted;
                        return (
                          <OrderMenuProductCard
                            key={slot.id}
                            product={product}
                            quantity={inCart}
                            allergens={productAllergensMap[product.id] ?? product.allergens ?? []}
                            customerAllergenIds={customerAllergenIds}
                            exhausted={exhausted}
                            addDisabled={addDisabled}
                            switchEligible={slot.isSwitch}
                            switchSelected={cartForDate[product.id]?.isSwitch ?? false}
                            onToggleSwitch={() => toggleSwitch(product.id)}
                            onAdd={() => addToCart(slot)}
                            onRemove={() => decrement(product.id)}
                          />
                        );
                      })}
                    </Stack>
                  </Box>
                ))}
              </Stack>
            ) : null}

            {allergenConflicts.length > 0 ? (
              <Alert severity="warning">
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Attention — allergènes déclarés sur votre compte :
                </Typography>
                {allergenConflicts.map((line) => (
                  <Typography key={line} variant="caption" component="div">
                    {line}
                  </Typography>
                ))}
              </Alert>
            ) : null}

            {allCartItems.length > 0 ? (
              <Paper sx={{ p: 1.5, position: "sticky", bottom: 80, zIndex: 5 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, textAlign: "center" }}>
                  {totalQuantity} article(s) · {grandTotal.toFixed(2)} €
                  {serviceType === "MEAL_DELIVERY" && deliveryTotal > 0
                    ? ` (dont livraison ${deliveryTotal.toFixed(2)} €)`
                    : ""}
                </Typography>
              </Paper>
            ) : null}
          </Stack>
        ) : null}

        {activeStep === 2 ? (
          <Stack spacing={2}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Récapitulatif
            </Typography>
            <Paper sx={{ p: 2 }}>
              <Stack spacing={0.5}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {serviceType === "CATERING"
                    ? "Retrait sur place"
                    : "Livraison à domicile"}
                </Typography>
                {serviceType === "MEAL_DELIVERY" && selectedAddress ? (
                  <Typography variant="caption" color="text.secondary">
                    {selectedAddress.address}
                    {selectedAddress.city ? `, ${selectedAddress.city}` : ""}
                  </Typography>
                ) : null}
                {serviceType === "CATERING" && pickup ? (
                  <Typography variant="caption" color="text.secondary">
                    {pickup.name} · {pickup.address}
                  </Typography>
                ) : null}
                <Typography variant="caption" color="text.secondary">
                  Vous avez créé <strong>{cartDates.length}</strong> commande(s).
                </Typography>
              </Stack>
            </Paper>

            {cartDates.length === 0 ? (
              <Alert severity="warning">Aucun article sélectionné.</Alert>
            ) : (
              <Stack spacing={2}>
                {cartDates.map((date) => {
                  const items = Object.values(cartByDate[date] ?? {});
                  const daySubtotal = items.reduce((sum, it) => sum + it.unitPrice * it.quantity, 0);
                  const dayDeliveryFee =
                    serviceType === "MEAL_DELIVERY" ? deliveryFeePerOrder : 0;
                  const dayTotal = daySubtotal + dayDeliveryFee;
                  return (
                    <Paper key={date} sx={{ p: 2 }}>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="flex-start"
                        spacing={1}
                        sx={{ mb: 1 }}
                      >
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                          {format(parseISO(date), "EEEE d MMMM yyyy", { locale: fr })}
                        </Typography>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => editDayMenu(date)}
                        >
                          Modifier
                        </Button>
                      </Stack>
                      <Stack spacing={1} divider={<Divider />}>
                        {items.map((it) => {
                          const matchingAllergens = (productAllergensMap[it.productId] ?? []).filter(
                            (a) => customerAllergenIds.includes(a.id),
                          );
                          return (
                            <Stack
                              key={`${date}-${it.productId}`}
                              direction="row"
                              justifyContent="space-between"
                              alignItems="flex-start"
                            >
                              <Box sx={{ flex: 1, minWidth: 0, pr: 1 }}>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {it.productName}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {it.quantity} × {it.unitPrice.toFixed(2)} €
                                </Typography>
                                {it.isSwitch ? (
                                  <Typography
                                    variant="caption"
                                    sx={{ display: "block", color: "success.main", fontWeight: 600 }}
                                  >
                                    Formule Switch (légumes)
                                  </Typography>
                                ) : null}
                                {matchingAllergens.length > 0 ? (
                                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.75 }}>
                                    {matchingAllergens.map((allergen) => (
                                      <AllergenChip
                                        key={allergen.id}
                                        label={
                                          allergen.icon
                                            ? `${allergen.icon} ${allergen.name}`
                                            : allergen.name
                                        }
                                        compact
                                        sx={{
                                          bgcolor: "error.50",
                                          color: "error.dark",
                                          borderColor: "error.main",
                                          fontWeight: 700,
                                        }}
                                      />
                                    ))}
                                  </Box>
                                ) : null}
                              </Box>
                              <Typography variant="body2" sx={{ fontWeight: 600, flexShrink: 0 }}>
                                {(it.quantity * it.unitPrice).toFixed(2)} €
                              </Typography>
                            </Stack>
                          );
                        })}
                      </Stack>
                      <Divider sx={{ my: 1.5 }} />
                      <Stack spacing={0.75}>
                        <Stack direction="row" justifyContent="space-between">
                          <Typography variant="body2" color="text.secondary">
                            Sous-total produits
                          </Typography>
                          <Typography variant="body2">{daySubtotal.toFixed(2)} €</Typography>
                        </Stack>
                        {serviceType === "MEAL_DELIVERY" ? (
                          <Stack direction="row" justifyContent="space-between">
                            <Typography variant="body2" color="text.secondary">
                              Livraison
                              {selectedAddress?.zone?.name
                                ? ` (zone ${selectedAddress.zone.name})`
                                : ""}
                            </Typography>
                            <Typography variant="body2">
                              {dayDeliveryFee.toFixed(2)} €
                            </Typography>
                          </Stack>
                        ) : null}
                        <Stack direction="row" justifyContent="space-between">
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            Total (jour)
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {dayTotal.toFixed(2)} €
                          </Typography>
                        </Stack>
                      </Stack>
                      <TextField
                        label="Instructions pour ce jour (optionnel)"
                        multiline
                        minRows={2}
                        size="small"
                        value={instructionsByDate[date] ?? ""}
                        onChange={(e) => setDayInstructions(date, e.target.value)}
                        fullWidth
                        sx={{ mt: 1.5 }}
                      />
                    </Paper>
                  );
                })}
              </Stack>
            )}

            <Paper sx={{ p: 2 }}>
              <Stack spacing={1}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Sous-total produits ({cartDates.length} jour{cartDates.length > 1 ? "s" : ""})
                  </Typography>
                  <Typography variant="body2">{subtotal.toFixed(2)} €</Typography>
                </Stack>
                {serviceType === "MEAL_DELIVERY" ? (
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Frais de livraison ({cartDates.length} × {deliveryFeePerOrder.toFixed(2)} €)
                    </Typography>
                    <Typography variant="body2">{deliveryTotal.toFixed(2)} €</Typography>
                  </Stack>
                ) : null}
                <Divider />
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Total estimé
                  </Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                    {grandTotal.toFixed(2)} €
                  </Typography>
                </Stack>
                {serviceType === "MEAL_DELIVERY" && deliveryFeePerOrder === 0 ? (
                  <Typography variant="caption" color="text.secondary">
                    Aucun forfait de livraison configuré pour votre zone.
                  </Typography>
                ) : null}
              </Stack>
            </Paper>

            {allergenConflicts.length > 0 ? (
              <Alert severity="warning">
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Attention — allergènes déclarés sur votre compte :
                </Typography>
                {allergenConflicts.map((line) => (
                  <Typography key={line} variant="caption" component="div">
                    {line}
                  </Typography>
                ))}
              </Alert>
            ) : null}
          </Stack>
        ) : null}

        {activeStep === 3 ? (
          <Stack spacing={2}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Confirmer la commande
            </Typography>
            {canChoosePaymentTiming ? (
              <Paper sx={{ p: 2 }}>
                <FormControl>
                  <FormLabel sx={{ fontWeight: 600, mb: 1 }}>Mode de règlement</FormLabel>
                  <RadioGroup
                    value={paymentTiming}
                    onChange={(e) =>
                      setPaymentTiming(e.target.value as "IMMEDIATE" | "ON_RECEPTION")
                    }
                  >
                    <FormControlLabel
                      value="IMMEDIATE"
                      control={<Radio />}
                      label={
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            Régler immédiatement
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Paiement en ligne sécurisé par carte bancaire.
                          </Typography>
                        </Box>
                      }
                    />
                    <FormControlLabel
                      value="ON_RECEPTION"
                      control={<Radio />}
                      label={
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            Régler à réception
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Vous réglerez au moment de la{" "}
                            {serviceType === "CATERING" ? "récupération" : "livraison"}.
                          </Typography>
                        </Box>
                      }
                    />
                  </RadioGroup>
                </FormControl>
              </Paper>
            ) : (
              <Alert severity="info">
                Votre compte est en paiement différé : la commande sera enregistrée et validée
                par notre équipe.
              </Alert>
            )}
            <Paper sx={{ p: 2 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Total estimé ({cartDates.length} commande{cartDates.length > 1 ? "s" : ""})
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, color: "secondary.main" }}>
                  {grandTotal.toFixed(2)} €
                </Typography>
              </Stack>
            </Paper>
            {cartDates.length > 0 ? (
              <Stack spacing={1}>
                {cartDates.map((date) => {
                  const instructions = instructionsByDate[date]?.trim();
                  if (!instructions) return null;
                  return (
                    <Paper key={`instr-${date}`} variant="outlined" sx={{ p: 1.5 }}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {format(parseISO(date), "EEEE d MMMM", { locale: fr })}
                      </Typography>
                      <Typography variant="body2">{instructions}</Typography>
                    </Paper>
                  );
                })}
              </Stack>
            ) : null}
            {submitError ? <Alert severity="error">{submitError}</Alert> : null}
          </Stack>
        ) : null}

        {/* Navigation */}
        <Stack direction="row" justifyContent="space-between" sx={{ mt: 2 }}>
          <Button onClick={handleBack} disabled={activeStep === 0 || submitting}>
            Précédent
          </Button>
          {activeStep < 3 ? (
            <Button
              onClick={handleNext}
              variant="contained"
              color="secondary"
              disabled={!canGoNext()}
            >
              Suivant
            </Button>
          ) : (
            <Button
              variant="contained"
              color="secondary"
              onClick={handleConfirm}
              disabled={submitting}
            >
              {submitting
                ? payNow
                  ? "Redirection…"
                  : "Envoi…"
                : payNow
                  ? `Payer ${grandTotal.toFixed(2)} €`
                  : "Confirmer la commande"}
            </Button>
          )}
        </Stack>
      </Stack>
    </ClientLayout>
  );
}
